export type ClarificationField =
  | 'goal'
  | 'audience'
  | 'scope'
  | 'data'
  | 'constraints'
  | 'output'

export type ClarificationDraft = Record<ClarificationField, string> & {
  assumptions: string
  outOfScope: string
}

export type ClarificationQuestion = {
  field: ClarificationField
  question: string
  reason: string
}

export type ClarificationAssessment = {
  pass: boolean
  missing: ClarificationField[]
  questions: ClarificationQuestion[]
  remainingAfterFirstRound: ClarificationField[]
}

const FIELD_META: Array<{
  field: ClarificationField
  label: string
  question: string
  reason: string
}> = [
  {
    field: 'goal',
    label: 'Mục tiêu',
    question: 'Kết quả cuối cùng cần đạt là gì?',
    reason: 'Thiếu mục tiêu có thể làm thay đổi toàn bộ cách triển khai và tiêu chí nghiệm thu.',
  },
  {
    field: 'output',
    label: 'Đầu ra',
    question: 'Đầu ra cụ thể cần bàn giao là gì?',
    reason: 'Thiếu đầu ra khiến hệ thống không thể xác định điều kiện hoàn thành.',
  },
  {
    field: 'scope',
    label: 'Phạm vi',
    question: 'Phạm vi làm và không làm của công việc này là gì?',
    reason: 'Thiếu phạm vi có thể làm AI thực hiện thừa hoặc bỏ sót phần quan trọng.',
  },
  {
    field: 'data',
    label: 'Dữ liệu',
    question: 'Dữ liệu hoặc nguồn đầu vào bắt buộc nào phải được sử dụng?',
    reason: 'Thiếu dữ liệu nguồn có thể làm kết quả không kiểm chứng được hoặc sai ngữ cảnh.',
  },
  {
    field: 'constraints',
    label: 'Ràng buộc',
    question: 'Có ràng buộc nào về thời gian, chi phí, quyền hạn, bảo mật hoặc an toàn?',
    reason: 'Thiếu ràng buộc có thể dẫn đến hành động vượt quyền hoặc vi phạm DoD.',
  },
  {
    field: 'audience',
    label: 'Đối tượng',
    question: 'Ai sẽ sử dụng, nhận hoặc nghiệm thu đầu ra?',
    reason: 'Thiếu đối tượng có thể làm sai mức chi tiết, định dạng hoặc quyền quyết định.',
  },
]

export const clarificationLabels = Object.fromEntries(
  FIELD_META.map(({ field, label }) => [field, label]),
) as Record<ClarificationField, string>

export const platformClarificationConstraints = [
  'D-032: factual information must be verified; unresolved facts are KHÔNG RÕ.',
  'D-033: only material gaps are clarified; first round is capped at 5 questions.',
  'D-041/D-042: routine technical autonomy is allowed, but FG-01..FG-11 remain mandatory.',
  'MVP: zero new spend and no production/live change without Founder approval.',
] as const

export function canEditClarificationState(state: string): boolean {
  return ['DRAFT', 'NEEDS_CLARIFICATION', 'READY'].includes(state)
}

function clean(value: string) {
  return value.trim()
}

export function createClarificationDraft(rawIntent: string): ClarificationDraft {
  return {
    goal: clean(rawIntent),
    audience: '',
    scope: '',
    data: '',
    constraints: '',
    output: '',
    assumptions: '',
    outOfScope: '',
  }
}

export function assessClarification(
  draft: ClarificationDraft,
): ClarificationAssessment {
  const missing = FIELD_META.filter(({ field }) => !clean(draft[field])).map(
    ({ field }) => field,
  )
  const firstRound = missing.slice(0, 5)

  return {
    pass: missing.length === 0,
    missing,
    questions: firstRound.map((field) => {
      const meta = FIELD_META.find((item) => item.field === field)
      if (!meta) throw new Error(`Unknown clarification field: ${field}`)
      return {
        field,
        question: meta.question,
        reason: meta.reason,
      }
    }),
    remainingAfterFirstRound: missing.slice(5),
  }
}

export function parseLines(value: string): string[] {
  return value
    .split(/\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean)
}

export function buildClarificationSummary(draft: ClarificationDraft) {
  const understood = FIELD_META.filter(({ field }) => clean(draft[field])).map(
    ({ field, label }) => `${label}: ${clean(draft[field])}`,
  )

  const assumptions = parseLines(draft.assumptions).map(
    (item) => `Cần xác nhận — ${item}`,
  )

  const outOfScope = parseLines(draft.outOfScope)
  const expectedOutput = clean(draft.output)

  return {
    understood,
    assumptions,
    outOfScope:
      outOfScope.length > 0
        ? outOfScope
        : ['Không có mục ngoài phạm vi được ghi riêng; xem trường Phạm vi.'],
    expectedOutput: expectedOutput || 'KHÔNG RÕ',
  }
}

export function isClarificationDraft(value: unknown): value is ClarificationDraft {
  if (!value || typeof value !== 'object') return false
  const record = value as Record<string, unknown>
  return (
    [...FIELD_META.map(({ field }) => field), 'assumptions', 'outOfScope'].every(
      (key) => typeof record[key] === 'string',
    )
  )
}
